import { Navigate } from 'react-router-dom';
import type { UserRole } from '@wanderlust/shared';
import { STAFF_ROLES } from '@wanderlust/shared';
import { useAuth } from '../hooks/useAuth';
import { RequireAuth } from './RequireAuth';

/**
 * Renders children only for the listed roles. Kept separate from the exported
 * guards so the role check runs after RequireAuth has resolved loading and
 * confirmed a user.
 */
function RoleOnly({ roles, children }: { roles: readonly UserRole[]; children: React.ReactNode }) {
  const { user } = useAuth();

  if (!user?.role || !roles.includes(user.role)) {
    return <Navigate to="/trips" replace />;
  }

  return <>{children}</>;
}

/**
 * Route guard for /admin/* routes: requires a signed-in user (via RequireAuth)
 * and the admin role. Pages like TripView and TripEditor switch into admin mode
 * purely on the URL prefix, so this guard is what keeps a traveler from loading
 * them by typing an /admin URL.
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <RoleOnly roles={['admin']}>{children}</RoleOnly>
    </RequireAuth>
  );
}

/**
 * Route guard for the admin pages a marketer shares with the admin: the blog,
 * the destination knowledge base and the social media portal. Everything else
 * under /admin stays on RequireAdmin.
 */
export function RequireStaff({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <RoleOnly roles={STAFF_ROLES}>{children}</RoleOnly>
    </RequireAuth>
  );
}
